<script setup>
import { computed } from 'vue'
import { State } from 'country-state-city'
import { getAdminDivisionLabel } from '@/helpers/general'
import {
  TransitionRoot,
  Combobox, ComboboxInput, ComboboxOptions, ComboboxOption
} from '@headlessui/vue'

const props = defineProps({
  location: { type: Object, required: true },
  errors: { type: Array, default: [] },
  editMode: { type: Boolean, default: true },
  hideLabel: { type: Boolean, default: false }
})

const query = defineModel('query', { default: '' })

const adminDivisionLabel = computed(() => getAdminDivisionLabel(props.location.country))

const filteredStates = computed(() => {
  return query.value === ''
    ? State.getStatesOfCountry(props.location.country)
    : State.getStatesOfCountry(props.location.country).filter((state) =>
        state.name
          .toLowerCase()
          .replace(/\s+/g, '')
          .includes(query.value.toLowerCase().replace(/\s+/g, '')) ||
        state.isoCode
          .toLowerCase()
          .replace(/\s+/g, '')
          .includes(query.value.toLowerCase().replace(/\s+/g, ''))
      )
})
</script>

<template>
  <label class="block">
    <span class="controller-label" v-if="!hideLabel">{{ adminDivisionLabel }}</span>
    <template v-if="editMode">
      <Combobox v-model="location.state">
        <ComboboxInput
          @change="query = $event.target.value"
          class="input-controller font-normal py-2"
          :class="{'error': errors.includes('state')}"
          :placeholder="adminDivisionLabel"
        />
        <small v-if="errors.includes('state')" class="controller-error-msg text-xs">
          <span>This field is required</span>
        </small>
        <TransitionRoot
          leave="transition ease-in duration-100"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
          @after-leave="query = ''"
        >
          <ComboboxOptions static class="border border-tint-1 rounded-lg">
            <ComboboxOption as="template" v-for="(state, index) in filteredStates" :key="index" v-slot="{ active }" :value="state.name">
              <li
                class="flex items-center justify-between p-3 transition duration-300 hover:bg-blue-50 cursor-pointer group"
                :class="{ 'bg-blue-50 rounded-lg': active }"
              >
                {{ `${state.name}, ${state.isoCode}` }}
              </li>
            </ComboboxOption>
          </ComboboxOptions>
        </TransitionRoot>
      </Combobox>
    </template>
    <p class="h-9 leading-9 text-sm text-gray-900" :class="{ 'text-gray-400': !location.state }" v-else>{{ location.state || '-' }}</p>
  </label>
</template>
